// The incremental half of the tiered PostgreSQL snapshot load (SLICES.md § S7
// items 1 + 2, engine parity from item 8). `pgSchemaCache.ts` owns the cache
// entry, the TTL and the phase orchestration; this module owns the DATA
// STRUCTURE those phases fill in — a live snapshot that keeps the native cache
// shape and its bus projection in lockstep as pages arrive.
//
// Mirrors `mssqlSnapshotMerge.ts`, on top of the engine-neutral
// `liveSnapshotMerge.ts` scaffolding: relation entries and their projections
// share an index, so a page re-projects only the relations it touched.

import type {
  FkEdge,
  PropertyGraphMeta,
  RoutineMeta,
  SchemaSnapshot,
  SequenceMeta,
  TableMeta,
  UserTypeMeta,
} from '@/core/engines/intellisenseTypes';
import type { RoutineMetadata } from '@/types/electron';
import { projectPgTableEntry } from '@/features/intellisense/schemaBus/adapters/pgsqlAdapter';
import { projectRoutines } from '@/features/intellisense/schemaBus/adapters/projectionHelpers';

import {
  applyGlobalPage,
  applyPerRelationPage,
  claimGlobalReplace,
  createLiveSnapshot,
  type LiveSnapshot,
} from './liveSnapshotMerge';
import { pgTableKey } from './pgRelationKey';
import type { PgColumnIntrospection } from './pgSchemaIntrospection';
import type { PgSchemaSnapshot, PgSchemaTableEntry, PgUserType } from './pgSchemaTypes';

export type PgLiveSnapshot = LiveSnapshot<PgSchemaSnapshot, PgSchemaTableEntry> & {
  /** Mutable arrays shared by identity with BOTH `native` and `projected`. */
  readonly foreignKeys: FkEdge[];
  readonly sequences: SequenceMeta[];
  readonly userTypes: PgUserType[];
  readonly propertyGraphs: PropertyGraphMeta[];
  readonly extensions: string[];
  /** Bus-shaped mirrors that need a mapping step rather than shared identity. */
  readonly projectedRoutines: RoutineMeta[];
  readonly projectedUserTypes: UserTypeMeta[];
  /** `columnsByTableLower` buckets this refresh has already reset. */
  readonly resetColumnBuckets: Set<string>;
};

export { pgTableKey } from './pgRelationKey';

/**
 * Carry the previous refresh's columns onto the freshly fetched relation list
 * — stale-while-revalidate. Without this, every forced refresh would blank
 * every relation's columns for as long as phase 2 takes, and a harness that
 * force-refreshes in a loop could never observe a column.
 *
 * A matview arrives from phase 1 WITH its `pg_attribute` columns, so only
 * column-less entries are filled in.
 */
export function carryOverPgColumns(
  previous: readonly PgSchemaTableEntry[] | undefined,
  fresh: readonly PgSchemaTableEntry[],
): PgSchemaTableEntry[] {
  if (!previous || previous.length === 0) return fresh.map((e) => ({ ...e }));
  const byKey = new Map<string, PgSchemaTableEntry>();
  for (const entry of previous) byKey.set(pgTableKey(entry.schema, entry.name), entry);
  return fresh.map((entry) => {
    if (entry.columns.length > 0) return { ...entry };
    const prior = byKey.get(pgTableKey(entry.schema, entry.name));
    if (!prior || prior.columns.length === 0) return { ...entry };
    return { ...entry, columns: prior.columns.slice() };
  });
}

/**
 * Build the live snapshot for a freshly fetched relation list, projecting the
 * relations in event-loop-yielding chunks so even a 200k-relation catalog
 * never occupies the main thread for a single long task.
 */
export async function createPgLiveSnapshot(
  native: PgSchemaSnapshot,
  options?: { readonly yieldBetweenChunks?: () => Promise<void> },
): Promise<PgLiveSnapshot> {
  const foreignKeys = (native.foreignKeys ?? []).slice();
  const sequences = (native.sequences ?? []).slice();
  const userTypes = (native.userTypes ?? []).slice();
  const propertyGraphs = (native.propertyGraphs ?? []).slice();
  const extensions = (native.extensions ?? []).slice();
  // Share the mutable arrays with the native snapshot so `getCachedPgSchema`
  // callers see phase 2 fill in without a second copy.
  native.foreignKeys = foreignKeys;
  native.sequences = sequences;
  native.userTypes = userTypes;
  native.propertyGraphs = propertyGraphs;
  native.extensions = extensions;

  const projectedRoutines: RoutineMeta[] = projectRoutines(
    native.routinesByNameLower,
    native.database,
  );
  const projectedUserTypes: UserTypeMeta[] = userTypes.map((t) => ({
    name: t.name,
    schema: t.schema,
    kind: t.kind,
  }));

  const schemaSet = new Set<string>();
  for (const entry of native.tables) schemaSet.add(entry.schema);

  const base = await createLiveSnapshot<PgSchemaSnapshot, PgSchemaTableEntry>({
    native,
    entries: native.tables,
    keyOf: (entry) => pgTableKey(entry.schema, entry.name),
    projectEntry: (entry) => projectPgTableEntry(entry, native.database),
    buildProjected: (tables: TableMeta[]): SchemaSnapshot => ({
      engineId: 'pgsql',
      connectionId: native.connectionId,
      database: native.database,
      schemas: Array.from(schemaSet).sort(),
      tables,
      routines: projectedRoutines,
      foreignKeys,
      // An unreadable search_path is left out, so the resolver falls back to
      // PostgreSQL's default (like the VQE); an empty one that was read stays.
      ...(native.searchPathKnown === false ? {} : { searchPath: native.searchPath }),
      extensions,
      serverVersion: native.serverVersion || undefined,
      // Unlike the one-shot `projectPgSnapshot`, the live projection always
      // carries these arrays: they are shared by identity so phase 2 can fill
      // them in, and an empty array reads the same as an absent one to every
      // consumer (`snapshot.userTypes?.length`).
      userTypes: projectedUserTypes,
      sequences,
      propertyGraphs,
      builtAt: native.builtAt,
    }),
    yieldBetweenChunks: options?.yieldBetweenChunks,
  });
  // `native.tables` must be the SAME array the live snapshot indexes, so a
  // consumer reading the native cache sees phase 2's column merges.
  native.tables = base.entries;

  return {
    ...base,
    foreignKeys,
    sequences,
    userTypes,
    propertyGraphs,
    extensions,
    projectedRoutines,
    projectedUserTypes,
    resetColumnBuckets: new Set<string>(),
  };
}

/**
 * Merge one page of column rows, replacing carried-over columns relation by
 * relation. The legacy bare-name `columnsByTableLower` index is kept in step:
 * its bucket is reset the first time this refresh writes to it, then appended
 * to — which reproduces the one-shot build's behaviour of merging same-named
 * relations from different schemas into one bucket.
 */
export function applyPgColumnPage(
  live: PgLiveSnapshot,
  columns: readonly PgColumnIntrospection[],
): void {
  applyPerRelationPage(live, 'columns', columns, {
    keyOf: (c) => pgTableKey(c.schema, c.table),
    append: (entry, list, replace) => {
      const projected = list.map((c) => c.column);
      // Never mutates the existing array: a carried-over `columns` may still
      // be shared with the previous snapshot object a caller is holding.
      entry.columns = replace ? projected : [...entry.columns, ...projected];
    },
  });

  const byTable = live.native.columnsByTableLower;
  for (const item of columns) {
    const bucket = item.table.toLowerCase();
    if (!live.resetColumnBuckets.has(bucket)) {
      live.resetColumnBuckets.add(bucket);
      byTable[bucket] = [];
    }
    (byTable[bucket] ??= []).push(item.column);
  }
}

export function applyPgForeignKeyPage(live: PgLiveSnapshot, edges: readonly FkEdge[]): void {
  applyGlobalPage(live, 'foreignKeys', live.foreignKeys, edges);
}

export function applyPgSequencePage(
  live: PgLiveSnapshot,
  sequences: readonly SequenceMeta[],
): void {
  applyGlobalPage(live, 'sequences', live.sequences, sequences);
}

export function applyPgUserTypePage(live: PgLiveSnapshot, types: readonly PgUserType[]): void {
  applyGlobalPage(live, 'userTypes', live.userTypes, types);
  // `projectedUserTypes` is a mapped mirror, not a shared array, so it has to
  // be refilled rather than appended-to through the same helper.
  live.projectedUserTypes.length = 0;
  for (const t of live.userTypes) {
    live.projectedUserTypes.push({ name: t.name, schema: t.schema, kind: t.kind });
  }
}

/**
 * Routines arrive flat and are bucketed by lower-cased name. The first page of
 * a refresh drops the carried-over map (so a dropped routine disappears);
 * later pages append.
 */
export function applyPgRoutinePage(
  live: PgLiveSnapshot,
  routines: readonly RoutineMetadata[],
): void {
  if (claimGlobalReplace(live, 'routines')) {
    live.native.routinesByNameLower = {};
  }
  const byName = live.native.routinesByNameLower;
  for (const routine of routines) {
    (byName[routine.name.toLowerCase()] ??= []).push(routine);
  }
  live.projectedRoutines.length = 0;
  live.projectedRoutines.push(...projectRoutines(byName, live.native.database));
}

/** Whole-list replacements for the small probe-shaped artefacts. */
export function setPgExtensions(live: PgLiveSnapshot, extensions: readonly string[]): void {
  live.extensions.length = 0;
  live.extensions.push(...extensions);
}

export function setPgPropertyGraphs(
  live: PgLiveSnapshot,
  graphs: readonly PropertyGraphMeta[],
): void {
  live.propertyGraphs.length = 0;
  live.propertyGraphs.push(...graphs);
}
